import React from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import { Link } from 'react-router-dom';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';

const TeamHealthChecksStage4 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 4: Create Accountability and Momentum with Transparency" icon={<ChartBarIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <p className="mb-2">You need to do 1 thing:</p>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Maintain high visibility on progress of action items owned by teams, leadership and yourself/people & culture.</span>
            </li>
          </ul>
          <p className="mt-4">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <p className="text-gray-200 mb-4">
          So you've run a team health check, collected the insights, made them visible across the org and teams, leadership and people & culture have potential actions. What's next?
        </p>

        <p className="text-gray-200 mb-4">
          Accountability and follow through.
        </p>

        <p className="text-gray-200 mb-4">
          How do we create this?
        </p>

        <p className="text-gray-200">
          Let's get into it
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Action: Increase visibility to keep action items top of mind</h3>
        <p className="text-gray-200 mb-4">
          You need something that makes it clear what actions from the team health check are actually being worked on, which are stalled, and what new issues might be emerging.
        </p>
        <p className="text-gray-200 mb-4">
          A good way to do this is to set up a monthly feedback loop, which functions both as a progress check and as an ongoing dialogue to foster continuous improvement. Here's how you can effectively establish this cycle:
        </p>
        <h4 className="text-lg font-semibold text-white mb-3">Schedule regular memo/meeting and invite teams and leadership to comment/update</h4>
        <p className="text-gray-200 mb-4">
          Personally I like to do this as a memo, a simple notion page once a month but tooling will depend on what your teams already use. If folks use Shortcut, Basecamp etc to track their goals/projects then that's where ideally you'd also record your team health check action items.
        </p>
        <ExpandableCallout type="info" title="Tip:" className="mt-4">
          <p className="text-gray-200">
            You can bolt on to existing reporting cadence/medium that you use in your org if that's easier e.g. integrate with OKRs or reporting pack. The goal is just to ensure folks have visibility on progress or lack of.
          </p>
        </ExpandableCallout>
        <h4 className="text-lg font-semibold text-white mb-3 mt-6">Celebrate Progress</h4>
        <p className="text-gray-200 mb-2">Probably goes without saying but with any 'new thing' it's super important to recognise and celebrate achievements and milestones no matter how small.</p>
        <ul className="list-none space-y-2 mb-4">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Fun prize / shoutout / award for the team that manages to complete all their actions</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Leadership pinging slack when a decision has been made / one of their actions completed</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Shoutout by the team lead to celebrate their team for completing something that was raised as a result of running the health check</span>
          </li>
        </ul>
        <p className="text-gray-200 mb-4">You get the idea.</p>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Running health checks 2-n</h3>
        <p className="text-gray-200 mb-4">So you've made it all visible.</p>
        <p className="text-gray-200 mb-4">You've got some momentum.</p>
        <p className="text-gray-200 mb-4">Teams that haven't actioned anything yet are seeing other teams action stuff and that's making them want to action stuff.</p>
        <p className="text-gray-200 mb-4">It's working!</p>
        <p className="text-gray-200 mb-4">When you get to the end of a cycle and it's time to run the health check again (once a quarter is fine, twice a quarter works too) then it's a case of repeating through from <Link to="/playbooks/team-health-checks/stage-2" className="text-primary hover:text-primary-light">Stage 2: Support facilitation across all teams</Link> and when you get to the bit where you need to collect insights and publish the learnings, you can now add a rather splendid trend view to your mini dashboard:</p>
        <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/team-health-check/dashboard.png" alt="Trend dashboard example" />
        <p className="text-gray-200 mt-4">That's progress!</p>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Recap of the Final Stage</h3>
        <p className="text-gray-200 mb-4">
          By implementing these steps, you ensure that the insights gained from team health checks translate into actionable improvements. This final stage closes the loop on the health check process, ensuring that it is not a one-time exercise but a continuous cycle of assessment, action, and improvement. Nice work!
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Want to go back to the home page?</h3>
        <Link to="/playbooks/team-health-checks/key-info" className="text-primary hover:text-primary-light underline">Culture Playbook: Team Health Checks: An Alternative To Survey Fatigue</Link>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default TeamHealthChecksStage4; 